import { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";
import { ClientRole, RolesEnum } from "@/constants/enums/roles.enum";
import { i18nMessage } from "@/lib/i18nMessage";

const firstNameSchema = z
  .string({ required_error: i18nMessage("shared:forms.user.firstNameRequired") })
  .min(1, i18nMessage("shared:forms.user.firstNameRequired"));

const lastNameSchema = z
  .string({ required_error: i18nMessage("shared:forms.user.lastNameRequired") })
  .min(1, i18nMessage("shared:forms.user.lastNameRequired"));

const emailSchema = z
  .string({ required_error: i18nMessage("shared:forms.user.emailRequired") })
  .email({ message: i18nMessage("validation:invalidEmail") });

const mobilePhoneNumberSchema = z
  .string({ required_error: i18nMessage("shared:forms.user.mobilePhoneRequired") })
  .refine((mobile) => isValidPhoneNumber(mobile), {
    message: i18nMessage("shared:forms.user.mobilePhoneInvalid"),
  });

const fixedPhoneNumberSchema = z
  .string()
  .refine((phone) => !phone || phone === "" || phone === "+33" || isValidPhoneNumber(phone), {
    message: i18nMessage("shared:forms.user.fixedPhoneInvalid"),
  })
  .transform((value) => (value === "+33" || value === "" ? undefined : value))
  .optional();

const passwordSchema = z
  .string({ required_error: i18nMessage("shared:forms.user.passwordRequired") })
  .min(8, i18nMessage("validation:passwordMinLength"));

const confirmPasswordSchema = z
  .string({ required_error: i18nMessage("shared:forms.user.confirmPasswordRequired") })
  .min(8, i18nMessage("validation:passwordMinLength"));

export const allRolesSchema = z.nativeEnum(RolesEnum, {
  message: i18nMessage("shared:forms.user.roleRequired"),
});

export const clientRolesSchema = z.enum(["ADMIN", "SUPERADMIN", "MEMBER", "ACCOUNTANT"] as const, {
  message: i18nMessage("shared:forms.user.roleRequired"),
}) as z.ZodType<ClientRole>;

export const baseUserSchema = z.object({
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  email: emailSchema,
  mobilePhoneNumber: mobilePhoneNumberSchema,
  fixedPhoneNumber: fixedPhoneNumberSchema,
});

// Pour le backOffice (tous les rôles)
export const CreateUserSchema = baseUserSchema
  .extend({
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema,
    role: allRolesSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: i18nMessage("validation:passwordsMismatch"),
    path: ["confirmPassword"],
  });

export type CreateUserFormData = z.infer<typeof CreateUserSchema>;

// Pour le client (rôles limités)
export const CreateUserClientSchema = baseUserSchema
  .extend({
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema,
    role: clientRolesSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: i18nMessage("validation:passwordsMismatch"),
    path: ["confirmPassword"],
  });

export type CreateUserClientFormData = z.infer<typeof CreateUserClientSchema>;

// Pour le backOffice (tous les rôles)
export const UpdateUserSchema = baseUserSchema.extend({
  role: allRolesSchema,
});
export type UpdateUserFormData = z.infer<typeof UpdateUserSchema>;

// Pour le client (rôles limités)
export const UpdateUserClientSchema = baseUserSchema.extend({
  role: clientRolesSchema,
});

export const UpdateUserPasswordSchema = z
  .object({
    password: passwordSchema,
    confirmPassword: confirmPasswordSchema,
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: i18nMessage("validation:passwordsMismatch"),
    path: ["confirmPassword"],
  });

export type UpdateUserPasswordFormData = z.infer<typeof UpdateUserPasswordSchema>;

export const DEFAULT_CREATE_USER: Partial<CreateUserFormData> = {
  lastName: "",
  firstName: "",
  email: "",
  fixedPhoneNumber: "",
  mobilePhoneNumber: "",
  password: "",
  confirmPassword: "",
  role: undefined,
};

export const DEFAULT_CREATE_USER_CLIENT: Partial<CreateUserClientFormData> = {
  lastName: "",
  firstName: "",
  email: "",
  fixedPhoneNumber: "",
  mobilePhoneNumber: "",
  password: "",
  confirmPassword: "",
  role: undefined,
};

export const DEFAULT_UPDATE_USER_PASSWORD: UpdateUserPasswordFormData = {
  password: "",
  confirmPassword: "",
};

export type UserForUpdate = {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  mobilePhoneNumber: string;
  fixedPhoneNumber?: string | null;
  role: string;
};

export const buildDefaultValuesUpdateUser = (user: UserForUpdate): UpdateUserFormData => ({
  lastName: user.lastName,
  firstName: user.firstName,
  email: user.email,
  mobilePhoneNumber: user.mobilePhoneNumber,
  fixedPhoneNumber: user.fixedPhoneNumber ?? undefined,
  role: user.role as RolesEnum,
});
